/**
 * Multi-select filter pill. Click the activator to open a popover with
 * a search box + checkbox list. Selected values appear as `Label (n)`
 * on the activator. Used inline in the always-visible filter bar — no
 * "Add filter +" indirection.
 */

import { useMemo, useState } from 'react';
import {
  Popover, Button, ChoiceList, TextField, BlockStack, InlineStack, Box,
} from '@shopify/polaris';

export interface MultiSelectOption {
  value: string;
  label: string;
  // Optional badge count rendered inline (e.g. "Nitto (152)").
  count?: number;
}

export interface MultiSelectFilterProps {
  label: string;
  selected: string[];
  options: MultiSelectOption[];
  onChange: (next: string[]) => void;
  /** When the option list is large (>20), show an inline search box. */
  searchable?: boolean;
  /** Emit lowercase-compare matches for searchable lists. */
  searchPlaceholder?: string;
}

export function MultiSelectFilter({
  label, selected, options, onChange, searchable, searchPlaceholder,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) =>
      o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, search]);

  const choices = filtered.map((o) => ({
    label: o.count != null ? `${o.label} (${o.count.toLocaleString()})` : o.label,
    value: o.value,
  }));

  const activatorLabel =
    selected.length === 0
      ? label
      : selected.length === 1
        ? `${label}: ${selected[0]}`
        : `${label} (${selected.length})`;

  return (
    <Popover
      active={open}
      onClose={() => setOpen(false)}
      activator={
        <Button
          disclosure
          pressed={selected.length > 0}
          onClick={() => setOpen((v) => !v)}
        >
          {activatorLabel}
        </Button>
      }
    >
      <Box padding="300" minWidth="240px">
        <BlockStack gap="200">
          {searchable && (
            <TextField
              label=""
              labelHidden
              placeholder={searchPlaceholder ?? `Search ${label.toLowerCase()}…`}
              value={search}
              onChange={setSearch}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setSearch('')}
            />
          )}
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <ChoiceList
              title=""
              titleHidden
              allowMultiple
              choices={choices}
              selected={selected}
              onChange={onChange}
            />
          </div>
          {selected.length > 0 && (
            <InlineStack gap="200" align="space-between">
              <Button variant="plain" onClick={() => onChange([])}>
                Clear
              </Button>
              <Button variant="plain" onClick={() => setOpen(false)}>
                Done
              </Button>
            </InlineStack>
          )}
        </BlockStack>
      </Box>
    </Popover>
  );
}
